// File: admin-react/src/components/CustomAssessment/CustomAssessmentSectionConfigTable.js

import React from 'react';
import { Table, InputNumber, Input } from 'antd';

// `value` is keyed by section_id. Section name is informational only — the same
// name can appear on multiple sections, so id is the only safe unique key.
const CustomAssessmentSectionConfigTable = ({
  sections = [],
  // { [section_id]: { section_name, numQuestions, totalMarks, marksPerQuestion, sectionTime, displayName } }
  value = {},
  onChange,
  validationErrors = {},
}) => {
  const handleFieldChange = (sectionId, sectionName, field, fieldValue) => {
    const numeric = field === 'displayName' ? fieldValue : (Number(fieldValue) || 0);

    const current = value[sectionId] || {
      section_name: sectionName,
      numQuestions: 0,
      totalMarks: 0,
      marksPerQuestion: 0,
      sectionTime: 30,
      displayName: sectionName,
    };

    const updated = {
      ...current,
      section_name: sectionName,
      [field]: field === 'displayName' ? fieldValue : numeric,
    };

    // Only recalculate marks if it's a number field
    if (field !== 'displayName') {
      const q = updated.numQuestions || 0;
      const m = updated.totalMarks || 0;
      updated.marksPerQuestion = q > 0 ? Number((m / q).toFixed(2)) : 0;
    }

    const nextConfig = {
      ...value,
      [sectionId]: updated,
    };

    if (onChange) onChange(nextConfig);
  };

  const columns = [
    {
      title: 'Section Name',
      dataIndex: 'section_name',
      key: 'section_name',
      render: (_, record) => {
        const cfg = value[record.section_id] || {};
        return (
          <Input
            placeholder={record.section_name}
            value={cfg.displayName || record.section_name}
            onChange={e =>
              handleFieldChange(record.section_id, record.section_name, 'displayName', e.target.value)
            }
            style={{ minWidth: 150 }}
          />
        );
      },
    },
    {
      title: 'Number of Questions',
      dataIndex: 'numQuestions',
      key: 'numQuestions',
      render: (_, record) => {
        const cfg = value[record.section_id] || {};
        const hasError = validationErrors[`customSectionConfig_${record.section_id}`];
        const exceedsError = validationErrors[`customSectionConfig_${record.section_id}_exceedsTotal`];

        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <InputNumber
              min={0}
              value={cfg.numQuestions}
              onChange={val =>
                handleFieldChange(record.section_id, record.section_name, 'numQuestions', val)
              }
              status={hasError || exceedsError ? 'error' : ''}
              style={{ width: '100%' }}
            />
            {exceedsError && (
              <span style={{ color: '#ff4d4f', fontSize: '11px', lineHeight: 1.2, marginTop: 4 }}>
                Max {record.totalQuestions}
              </span>
            )}
            {hasError && !exceedsError && (
              <span style={{ color: '#ff4d4f', fontSize: '11px', lineHeight: 1.2, marginTop: 4 }}>
                Required
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Total Marks',
      dataIndex: 'totalMarks',
      key: 'totalMarks',
      render: (_, record) => {
        const cfg = value[record.section_id] || {};
        const hasError = validationErrors[`customSectionConfig_${record.section_id}_marks`];

        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <InputNumber
              min={0}
              value={cfg.totalMarks}
              onChange={val =>
                handleFieldChange(record.section_id, record.section_name, 'totalMarks', val)
              }
              status={hasError ? 'error' : ''}
              style={{ width: '100%' }}
            />
            {hasError && (
              <span style={{ color: '#ff4d4f', fontSize: '11px', lineHeight: 1.2, marginTop: 4 }}>
                Required &gt; 0
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Section Time (mins)',
      dataIndex: 'sectionTime',
      key: 'sectionTime',
      render: (_, record) => {
        const cfg = value[record.section_id] || {};
        const hasError = validationErrors[`customSectionConfig_${record.section_id}_time`];

        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <InputNumber
              min={0}
              value={cfg.sectionTime ?? 30}
              onChange={val =>
                handleFieldChange(record.section_id, record.section_name, 'sectionTime', val)
              }
              status={hasError ? 'error' : ''}
              style={{ width: '100%' }}
            />
            {hasError && (
              <span style={{ color: '#ff4d4f', fontSize: '11px', lineHeight: 1.2, marginTop: 4 }}>
                Required &gt; 0
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Marks per Question',
      dataIndex: 'marksPerQuestion',
      key: 'marksPerQuestion',
      render: (_, record) => {
        const cfg = value[record.section_id] || {};
        return <span>{cfg.marksPerQuestion || 0}</span>;
      },
    },
  ];

  return (
    <Table
      rowKey="section_id"
      columns={columns}
      dataSource={sections}
      pagination={false}
      size="small"
    />
  );
};

export default CustomAssessmentSectionConfigTable;
